'use client';

import { useEffect, useState } from 'react';

interface ReadingProgressBarProps {
  progress: number;
  currentSection: string;
}

export function ReadingProgressBar({ progress, currentSection }: ReadingProgressBarProps) {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      // Show progress bar after scrolling past hero
      setIsVisible(window.scrollY > 200);
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  if (!isVisible) return null;
  
  return (
    <div className="fixed top-16 left-0 right-0 z-40 bg-white border-b border-gray-200 shadow-sm">
      <div className="relative h-12 flex items-center">
        {/* Progress Bar Background */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200">
          <div 
            className="h-full bg-gradient-to-r from-[#294bfd] to-[#ff34a7] transition-all duration-300 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
        
        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Progress Percentage */}
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-900">
                  {Math.round(progress)}%
                </span>
                <span className="text-sm text-gray-500">complete</span>
              </div>
              
              {/* Current Section */}
              {currentSection && (
                <>
                  <span className="text-gray-300">|</span>
                  <span className="text-sm text-gray-700 truncate max-w-md">
                    {currentSection}
                  </span>
                </>
              )}
            </div>
            
            {/* Quick Actions */}
            <div className="hidden md:flex items-center space-x-3">
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="text-sm text-gray-600 hover:text-primary transition-colors"
              >
                Back to top ↑
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}